import React from 'react';
import { motion } from 'framer-motion';
import { FileText, RefreshCw, MessageSquare, Pill, ClipboardCheck } from 'lucide-react';
export function ProductReveal() {
  return <section className="w-full py-20 px-6 bg-warm-white" data-id="element-237">
      <div className="max-w-5xl mx-auto" data-id="element-238">
        <div className="text-center mb-16" data-id="element-239">
          <motion.span initial={{
          opacity: 0
        }} whileInView={{
          opacity: 1
        }} viewport={{
          once: true
        }} className="text-sm font-bold tracking-widest text-muted uppercase" data-id="element-240">
            Introducing
          </motion.span>
          <motion.h2 initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-4xl md:text-5xl font-bold text-navy mt-3 mb-6" data-id="element-241">
            The ER‑Ready Mom Binder
            <span className="block text-2xl md:text-3xl font-normal text-muted mt-2" data-id="element-242">
              (24‑Hour Setup)
            </span>
          </motion.h2>
          <motion.p initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-lg md:text-xl text-body max-w-2xl mx-auto" data-id="element-243">
            A printable PDF kit that turns your child's medical reality into
            something you can hand over, read from, and update—even when you
            haven't slept.
          </motion.p>
        </div>

        {/* Mechanism Grid */}
        <div className="grid md:grid-cols-2 gap-8 mb-20" data-id="element-244">
          <motion.div initial={{
          opacity: 0,
          x: -20
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} className="bg-white p-8 rounded-2xl shadow-sm border border-soft-sage/50" data-id="element-245">
            <div className="w-12 h-12 bg-navy/10 rounded-full flex items-center justify-center mb-6" data-id="element-246">
              <FileText className="w-6 h-6 text-navy" data-id="element-247" />
            </div>
            <h3 className="text-2xl font-bold text-navy mb-4" data-id="element-248">
              The 1‑Page Medical Snapshot
            </h3>
            <p className="text-body mb-6" data-id="element-249">
              The one page that answers the questions the room asks first:
            </p>
            <ul className="space-y-3 text-sm md:text-base text-muted" data-id="element-250">
              <li className="flex items-center gap-2" data-id="element-251">
                <span className="w-1.5 h-1.5 rounded-full bg-teal" data-id="element-252" /> Who is
                your child?
              </li>
              <li className="flex items-center gap-2" data-id="element-253">
                <span className="w-1.5 h-1.5 rounded-full bg-teal" data-id="element-254" /> What
                matters most right now?
              </li>
              <li className="flex items-center gap-2" data-id="element-255">
                <span className="w-1.5 h-1.5 rounded-full bg-teal" data-id="element-256" /> What meds
                are current?
              </li>
              <li className="flex items-center gap-2" data-id="element-257">
                <span className="w-1.5 h-1.5 rounded-full bg-teal" data-id="element-258" /> What
                allergies/reactions exist?
              </li>
              <li className="flex items-center gap-2" data-id="element-259">
                <span className="w-1.5 h-1.5 rounded-full bg-teal" data-id="element-260" /> What's
                baseline vs not normal?
              </li>
            </ul>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          x: 20
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} className="bg-white p-8 rounded-2xl shadow-sm border border-soft-sage/50" data-id="element-261">
            <div className="w-12 h-12 bg-teal/10 rounded-full flex items-center justify-center mb-6" data-id="element-262">
              <RefreshCw className="w-6 h-6 text-teal" data-id="element-263" />
            </div>
            <h3 className="text-2xl font-bold text-navy mb-4" data-id="element-264">
              The Weekly Care Loop
            </h3>
            <p className="text-body mb-6" data-id="element-265">
              The reason this doesn't become a dead binder in a month. A
              15-minute reset that keeps it accurate without tracking your
              entire life.
            </p>
            <div className="bg-cream/50 p-4 rounded-lg" data-id="element-266">
              <p className="text-sm text-navy italic" data-id="element-267">
                "It's not about perfection. It's about having a system that
                survives the chaos of real life."
              </p>
            </div>
          </motion.div>
        </div>

        {/* Real Life Benefits */}
        <div className="max-w-3xl mx-auto" data-id="element-268">
          <h3 className="text-2xl font-bold text-center text-navy mb-12" data-id="element-269">
            What this lets you do (in real life)
          </h3>

          <div className="space-y-12" data-id="element-270">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} className="flex gap-6" data-id="element-271">
              <div className="flex-shrink-0 mt-1" data-id="element-272">
                <div className="w-10 h-10 rounded-full bg-navy text-white flex items-center justify-center font-bold font-serif" data-id="element-273">
                  1
                </div>
              </div>
              <div data-id="element-274">
                <h4 className="text-xl font-bold text-navy mb-2" data-id="element-275">
                  Walk into a visit and speak clearly—even if you're shaking.
                </h4>
                <p className="text-body mb-3" data-id="element-276">
                  You'll use a simple 30-second opening script that keeps the
                  room focused:
                </p>
                <div className="flex flex-wrap gap-2" data-id="element-277">
                  <span className="px-3 py-1 bg-white border border-soft-sage rounded-full text-sm text-muted" data-id="element-278">
                    Why you're here
                  </span>
                  <span className="px-3 py-1 bg-white border border-soft-sage rounded-full text-sm text-muted" data-id="element-279">
                    What changed
                  </span>
                  <span className="px-3 py-1 bg-white border border-soft-sage rounded-full text-sm text-muted" data-id="element-280">
                    What you need
                  </span>
                </div>
              </div>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            scale: 0.95
          }} whileInView={{
            opacity: 1,
            scale: 1
          }} viewport={{
            once: true
          }} className="my-12 rounded-xl overflow-hidden shadow-lg border border-soft-sage/30" data-id="element-281">
              <img src="https://cdn.magicpatterns.com/uploads/ddLEfWu8LSyssYSx785j8J/heylloyd-image-1771107517233.png" alt="The ER-Ready Mom Binder open on a table" className="w-full h-auto object-cover" data-id="element-282" />
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} className="flex gap-6" data-id="element-283">
              <div className="flex-shrink-0 mt-1" data-id="element-284">
                <div className="w-10 h-10 rounded-full bg-teal text-white flex items-center justify-center font-bold font-serif" data-id="element-285">
                  2
                </div>
              </div>
              <div data-id="element-286">
                <h4 className="text-xl font-bold text-navy mb-2" data-id="element-287">
                  Stop playing "medication roulette."
                </h4>
                <p className="text-body mb-3" data-id="element-288">
                  You'll keep a Medication Map that survives real-life chaos:
                  current meds (clean list), med history (what failed and why),
                  and a refill radar.
                </p>
              </div>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} className="flex gap-6" data-id="element-289">
              <div className="flex-shrink-0 mt-1" data-id="element-290">
                <div className="w-10 h-10 rounded-full bg-soft-sage text-navy flex items-center justify-center font-bold font-serif" data-id="element-291">
                  3
                </div>
              </div>
              <div data-id="element-292">
                <h4 className="text-xl font-bold text-navy mb-2" data-id="element-293">
                  Leave with a plan before you leave the building.
                </h4>
                <p className="text-body" data-id="element-294">
                  You'll use the Post‑Visit Action Sheet so you don't get home
                  and realize you don't know what to watch, what changed, or who
                  you follow up with.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>;
}